import { type LiveFeedErrorCode } from '../components/widgets/LiveFeed/liveFeedError';
import { type SdmLivePlayerState } from '../contexts/LiveVideoContext';

/** What a Live Feed widget shows at this moment. */
export interface LiveFeedStatus {
  /** The camera on screen, or null when no camera matched the widget's AI tags. */
  activeCameraId: string | null;
  /** How many cameras the widget rotates through. */
  cameraCount: number;
  /** The stream the active camera's player selected, or null before it selects one. */
  streamId: string | null;
  state: SdmLivePlayerState;
  /** Set only when `state` is `error`. */
  errorCode: LiveFeedErrorCode | null;
}

/** One widget's entry in the `liveFeeds` block of the SDM heartbeat. */
export interface LiveFeedHeartbeat extends LiveFeedStatus {
  widgetId: string;
  /** How many times the visible feed stopped to buffer after it started to play. */
  stallCount: number;
  /** The part of the reporting window, from 0 to 1, in which the feed played. */
  uptimeRatio: number;
}

interface Entry {
  registrations: number;
  status: LiveFeedStatus | undefined;
  statusSince: number;
  windowStart: number;
  playingMs: number;
  stallCount: number;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Collects the status of every Live Feed widget on screen, for the SDM heartbeat.
 *
 * Each `drain` covers the window since the previous `drain`: it reports the current status plus
 * the stalls and the playing time accumulated in that window, then starts a new window.
 */
export class LiveFeedTelemetry {
  private readonly entries = new Map<string, Entry>();

  /**
   * Error codes already sent for each widget, keyed by camera.
   *
   * Copies of one widget share this map. It stays after the last registration
   * ends, because a drag replaces the mounted copy and must not send the error again.
   */
  private readonly reportedErrors = new Map<string, Map<string, LiveFeedErrorCode>>();

  constructor(private readonly now: () => number = Date.now) {}

  /**
   * Start to track a widget.
   *
   * Registrations are counted because a dragged widget and its drag overlay copy can be mounted
   * at the same time. Both copies share one widget id and one entry.
   *
   * @returns A function that stops the tracking for this registration.
   */
  register(widgetId: string): () => void {
    const entry = this.entries.get(widgetId);
    if (entry) {
      entry.registrations += 1;
    } else {
      const now = this.now();
      this.entries.set(widgetId, {
        registrations: 1,
        status: undefined,
        statusSince: now,
        windowStart: now,
        playingMs: 0,
        stallCount: 0,
      });
    }

    return () => {
      const current = this.entries.get(widgetId);
      if (!current) return;
      current.registrations -= 1;
      if (current.registrations <= 0) this.entries.delete(widgetId);
    };
  }

  report(widgetId: string, status: LiveFeedStatus): void {
    const entry = this.entries.get(widgetId);
    if (!entry) return;
    this.accruePlayingTime(entry, this.now());
    entry.status = status;
  }

  recordStall(widgetId: string): void {
    const entry = this.entries.get(widgetId);
    if (entry) entry.stallCount += 1;
  }

  /**
   * Record an error code for one camera of a widget.
   *
   * `cameraKey` is empty when no camera matched the widget tags.
   *
   * @returns True when this camera has not already reported this code.
   */
  claimError(widgetId: string, cameraKey: string, errorCode: LiveFeedErrorCode): boolean {
    let byCamera = this.reportedErrors.get(widgetId);
    if (!byCamera) {
      byCamera = new Map();
      this.reportedErrors.set(widgetId, byCamera);
    }
    if (byCamera.get(cameraKey) === errorCode) return false;
    byCamera.set(cameraKey, errorCode);
    return true;
  }

  /** Forget a camera's error code so a later failure can be reported. */
  releaseError(widgetId: string, cameraKey: string): void {
    const byCamera = this.reportedErrors.get(widgetId);
    if (!byCamera) return;
    byCamera.delete(cameraKey);
    if (byCamera.size === 0) this.reportedErrors.delete(widgetId);
  }

  /** Report every widget that has a status, and start a new window for each. */
  drain(): LiveFeedHeartbeat[] {
    const now = this.now();
    const heartbeats: LiveFeedHeartbeat[] = [];

    for (const [widgetId, entry] of this.entries) {
      if (!entry.status) continue;

      this.accruePlayingTime(entry, now);
      const windowMs = now - entry.windowStart;
      // A zero-length window has no history, so the current state is the best measure.
      const uptimeRatio =
        windowMs > 0 ? entry.playingMs / windowMs : Number(entry.status.state === 'playing');

      heartbeats.push({
        widgetId,
        ...entry.status,
        stallCount: entry.stallCount,
        uptimeRatio: round3(uptimeRatio),
      });

      entry.windowStart = now;
      entry.playingMs = 0;
      entry.stallCount = 0;
    }

    return heartbeats;
  }

  private accruePlayingTime(entry: Entry, now: number): void {
    if (entry.status?.state === 'playing') entry.playingMs += now - entry.statusSince;
    entry.statusSince = now;
  }
}
